/**
 * Lightweight physics — for v1 we don't actually need Rapier. The world has
 * one walkable surface (the heightmap) and a few static collidable cylinders
 * (stage perimeters, fountain). A capsule-vs-heightmap step + a couple of
 * cylinder pushouts are plenty, and they let us skip the WASM init cost.
 *
 * If v2 needs ragdolls or vehicles, we swap this for Rapier without
 * touching PlayerController.
 */

import * as THREE from 'three';
import type { Terrain } from '@/world/Terrain';

export interface Collider {
  type: 'cylinder';
  x: number;
  z: number;
  radius: number;
  height: number;
}

export class PhysicsWorld {
  readonly gravity = -22; // m/s^2 — feels good for a foot game
  readonly playerHeight = 1.8;
  readonly playerRadius = 0.4;

  private colliders: Collider[] = [];

  constructor(public terrain: Terrain) {}

  addCylinder(x: number, z: number, radius: number, height: number): void {
    this.colliders.push({ type: 'cylinder', x, z, radius, height });
  }

  /** Returns ground height at world XZ. Used by PlayerController for snapping. */
  groundAt(x: number, z: number): number {
    return this.terrain.heightAt(x, z);
  }

  /**
   * Resolve a candidate move. `position` is updated in place.
   * Returns whether the player is grounded after the resolve.
   */
  resolve(position: THREE.Vector3, velocity: THREE.Vector3, dt: number): boolean {
    // Apply gravity to vertical component.
    velocity.y += this.gravity * dt;

    // Integrate.
    position.x += velocity.x * dt;
    position.y += velocity.y * dt;
    position.z += velocity.z * dt;

    // Push out of cylinder colliders.
    for (const c of this.colliders) {
      const dx = position.x - c.x;
      const dz = position.z - c.z;
      const d2 = dx * dx + dz * dz;
      const r = c.radius + this.playerRadius;
      if (d2 < r * r && d2 > 1e-6) {
        const ground = this.groundAt(c.x, c.z);
        if (position.y > ground + c.height) continue;
        const d = Math.sqrt(d2);
        const push = (r - d) / d;
        position.x += dx * push;
        position.z += dz * push;
        // Kill horizontal velocity into the obstacle.
        const vDot = (velocity.x * dx + velocity.z * dz) / d2;
        if (vDot < 0) {
          velocity.x -= vDot * dx;
          velocity.z -= vDot * dz;
        }
      }
    }

    // Snap to terrain.
    const ground = this.groundAt(position.x, position.z);
    let grounded = false;
    if (position.y <= ground + 0.01) {
      position.y = ground;
      if (velocity.y < 0) velocity.y = 0;
      grounded = true;
    }
    return grounded;
  }
}
